import { useState, useEffect } from 'react';

/**
 * Detekto platformën dhe menaxho install prompt të PWA-së.
 * Mbështet: iOS Safari, macOS Safari, Android Chrome/Samsung, Windows/Mac Chrome/Edge
 */
export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled]       = useState(false);
  const [isInstallable, setIsInstallable]   = useState(false);

  const ua = navigator.userAgent;

  // Platform detection
  const isIOS         = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid     = /Android/.test(ua);
  const isMac         = /Macintosh/.test(ua);
  const isSafari      = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgA/.test(ua);
  const isMacSafari   = isMac && isSafari;
  const isIOSSafari   = isIOS && isSafari;
  const needsManual   = isIOSSafari || isMacSafari; // platformë pa beforeinstallprompt

  const isInStandaloneMode =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;

  useEffect(() => {
    if (isInStandaloneMode) {
      setIsInstalled(true);
      return;
    }

    // iOS Safari dhe macOS Safari nuk kanë beforeinstallprompt – trego udhëzime manuale
    if (needsManual) {
      setIsInstallable(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isInStandaloneMode, needsManual]);

  const install = async () => {
    if (needsManual) return 'manual';
    if (!deferredPrompt) return 'no-prompt';
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return outcome;
  };

  return {
    isInstallable,
    isInstalled,
    isIOS: isIOSSafari,
    isMacSafari,
    isAndroid,
    needsManual,
    isInStandaloneMode,
    install,
  };
}
